// @vue/component
import { useStrategyDetailsStore } from "@/stores/CrossWalkAnalysis/strategyDetails";
import { useCrosswalkChallengeStore } from "@/stores/CrossWalkAnalysis/challenge";
import { storeToRefs } from "pinia";
import { MDBBtn, MDBTooltip } from "mdb-vue-ui-kit";
import { computed, ref, watchEffect } from "vue";

import { library } from "@fortawesome/fontawesome-svg-core";
import { FontAwesomeIcon } from "@fortawesome/vue-fontawesome";
import { faPlus } from "@fortawesome/free-solid-svg-icons";
import { faMinus } from "@fortawesome/free-solid-svg-icons";

library.add(faPlus);
library.add(faMinus);

export default {
  name: "SingleStrategySetup",

  components: { MDBBtn, MDBTooltip, FontAwesomeIcon },

  mixins: [],

  props: {},

  setup() {
    const strategyDetailsStore = useStrategyDetailsStore();
    const challengeStore = useCrosswalkChallengeStore();

    const { allStrategyDetails, setupStrategyId } = storeToRefs(strategyDetailsStore);
    const { editMode } = storeToRefs(challengeStore);

    const currentStrategy = computed(() => {
      const idx = setupStrategyId.value;
      const arr = allStrategyDetails.value || [];
      return idx != null ? (arr[idx] ?? null) : null;
    });

    // Tooltips
    const removesubtooltip = ref([]); // 2D: [strategyIdx][subIdx] -> boolean
    const addsubtooltip = ref([]);    // 1D: [strategyIdx] -> boolean
    const removetooltip = ref([]);    // unused in template currently, but kept
    const addtooltip = ref(false);    // unused in template currently, but kept

    // Ensure tooltip arrays always exist for the active strategy/substrategies
    watchEffect(() => {
      const sIdx = setupStrategyId.value ?? 0;
      if (!Array.isArray(removesubtooltip.value[sIdx])) {
        removesubtooltip.value[sIdx] = [];
      }
      if (addsubtooltip.value[sIdx] == null) {
        addsubtooltip.value[sIdx] = false;
      }

      const subCount = currentStrategy.value?.subStrategies?.length ?? 0;
      const row = removesubtooltip.value[sIdx];

      // expand
      while (row.length < subCount) row.push(false);
      // shrink
      if (row.length > subCount) row.splice(subCount);
    });

    return {
      strategyDetailsStore,
      allStrategyDetails,
      setupStrategyId,
      editMode,

      currentStrategy,

      // tooltip state
      removesubtooltip,
      addsubtooltip,
      removetooltip,
      addtooltip,
    };
  },

  data() {
    return {};
  },

  computed: {
    // If you need "current keyword" as a computed for the template
    currentKeyword() {
      return this.currentStrategy?.keyword || "";
    },
  },

  mounted() {
    // Keep the fetch, but DO NOT overwrite refs like the old version did.
    this.strategyDetailsStore.fetchCrosswalkAnalysis();
  },

  methods: {
    onOrderChanged() {
      const arr = this.allStrategyDetails || [];
      const movedIdx = this.setupStrategyId;

      if (arr[movedIdx] == null) return;

      // desired order comes from the dropdown (already bound via v-model.number)
      const desiredOrder = arr[movedIdx].order;
      this.applyOrderChange(movedIdx, desiredOrder);

      this.strategyDetailsStore.allStrategyDetailsChanged?.();
    },

    // Insert the edited item at the requested slot, then renumber 1..N
    applyOrderChange(movedIdx, desiredOrder) {
      const arr = this.allStrategyDetails || [];
      const n = arr.length;
      if (n <= 1) return;

      // Build a stable sequence of indices sorted by current order (fallback to i+1)
      const seq = arr
          .map((s, i) => ({ i, order: s.order ?? (i + 1) }))
          .sort((a, b) => (a.order === b.order ? a.i - b.i : a.order - b.order));

      // Clamp target position
      const targetPos = Math.min(Math.max(1, parseInt(desiredOrder, 10) || 1), n) - 1;

      // Remove the moved item from the sequence
      const fromPos = seq.findIndex((p) => p.i === movedIdx);
      if (fromPos === -1) return;
      const [moved] = seq.splice(fromPos, 1);

      // Insert at the desired position
      seq.splice(targetPos, 0, moved);

      // Renumber orders to be unique and sequential
      seq.forEach((p, idx) => {
        arr[p.i].order = idx + 1;
      });
    },

    normalizeStrategyOrders() {
      const arr = this.allStrategyDetails || [];
      if (!arr.length) return;

      const stable = arr
          .map((s, i) => ({ s, i }))
          .sort((a, b) => {
            const ao = a.s.order ?? 1e9,
                bo = b.s.order ?? 1e9;
            if (ao !== bo) return ao - bo;
            return a.i - b.i;
          });

      stable.forEach((wrap, idx) => {
        wrap.s.order = idx + 1;
      });
    },

    addSubStrategy(strategyIndex) {
      if (this.allStrategyDetails[strategyIndex] == null) {
        this.allStrategyDetails[strategyIndex] = { name: "", keyword: "", subStrategies: [] };
      }
      if (!Array.isArray(this.allStrategyDetails[strategyIndex].subStrategies)) {
        this.allStrategyDetails[strategyIndex].subStrategies = [];
      }
      this.allStrategyDetails[strategyIndex].subStrategies.push({ name: "", keyword: "" });
      this.strategyDetailsStore.allStrategyDetailsChanged();
    },

    removeSubStrategy(strategyIndex, subStrategyIndex) {
      const s = this.allStrategyDetails?.[strategyIndex];
      if (!s?.subStrategies?.length) return;

      s.subStrategies.splice(subStrategyIndex, 1);
      this.strategyDetailsStore.allStrategyDetailsChanged();
    },

    clickRemoveStrategy() {
      if (!Array.isArray(this.allStrategyDetails)) return;
      this.allStrategyDetails.splice(this.setupStrategyId, 1);
      this.strategyDetailsStore.allStrategyDetailsChanged();
    },
  },
};
